/** @jsxImportSource @emotion/react */

import { CSSObject } from '@emotion/react';
import layout from '../constants/layout';

// NOTES ON THIS COMPONENT:
// The need for this is explained fairly well here:
// https://nicolas-hoizey.com/articles/2015/02/18/viewport-height-is-taller-than-the-visible-part-of-the-document-in-some-mobile-browsers/
//
// OUR CUSTOM USE-CASE:
// Since the app renders within a scrollable frame on desktop,
// we want the height of the div to always be full height, no exceptions.
// On mobile, however, we're scrolling the actual document as usual,
// hence the div needs to be full height at the minimum, but should grow beyond that if needed.

interface IFullHeightDiv extends React.HTMLAttributes<HTMLElement> {
  canExceedOnMobile?: boolean;
}

const FullHeightDiv = ({
  canExceedOnMobile,
  children,
  ...props
}: IFullHeightDiv) => {
  const STYLE: CSSObject = {
    // Use CSS custom property for dynamic viewport height with fallbacks
    height: 'calc(var(--viewport-height, 100dvh))',
    minHeight: 'calc(var(--viewport-height, 100dvh))',

    // Fallback for browsers without dvh support
    '@supports not (height: 100dvh)': {
      height: 'var(--viewport-height, 100vh)',
      minHeight: 'var(--viewport-height, 100vh)',
    },

    // Override the height property on mobile if it needs to exceed full view height.
    ...(canExceedOnMobile && {
      [`@media (max-width: ${layout.BREAKPOINT_MEDIUM}px)`]: {
        height: 'auto !important',
        minHeight: 'calc(var(--viewport-height, 100dvh))',
      },
    }),
  };

  return (
    <div css={STYLE} {...props}>
      {children}
    </div>
  );
};

export default FullHeightDiv;
